import {
  differenceInCalendarDays,
  format,
  parseISO,
  startOfDay,
} from 'date-fns'
import React from 'react'
import { Spacing } from '@opallabs/gem'
import { EvenColumns } from '@opallabs/kuiper'
import { iterateDays } from '@opallabs/lambda'
import CardContext from '../cardContext'
import { WEEK_VIEW_SCROLL_OFFSET } from '../constants'
import { ContentCard } from '../ContentCard'
import { useContentDragContext } from '../ContentDragContext'
import { ContentQuickCreate } from '../ContentQuickCreate'
import { WeekViewCategory, WeekViewMode } from '../types'
import { useContentDropProposal } from '../useContentDropProposal'
import { useContentMap } from '../useMomentMap'
import { formatDateKey, formatCategoryKey } from '../useMomentMap/momentMap'
import { WeekViewGlobalContext } from '../weekViewGlobalContext'
import { useWeekViewSettings } from '../WeekViewSettingsContext'
import { CategoryColumn } from './CategoryColumn'
import { CategoryHeader } from './CategoryHeader'
import { ContentStack } from './ContentStack'
import { MomentStack } from './MomentStack'
import * as styles from './styles'

const NUMBER_OF_DAYS = 7
// Matches `DayHeaders`' right padding (reserved for the scrollbar tray)
// so span-mode day columns align with the header columns above.
const SCROLLBAR_RESERVE = Spacing.XS * 5

/**
 * One per day column. Drop preserves duration: Mon–Wed dropped on Thu
 * becomes Thu–Sat. `PostUpdater` converts both endpoints to intervals
 * server-side; no backend changes needed.
 */
const SpanDropTarget: React.FC<{ date: Date; column: number }> = ({
  date,
  column,
}) => {
  const { draggedContent, setDraggedContent, setHoveredDate } =
    useContentDragContext()
  const { onRescheduleContent } = React.useContext(CardContext) ?? {}

  const dayKey = format(date, 'yyyy-MM-dd')
  const { forbiddenReason, buildReschedulePayload } =
    useContentDropProposal(date)

  const onDragOver = (e: React.DragEvent) => {
    if (!draggedContent) return
    setHoveredDate(dayKey)
    if (forbiddenReason) return
    e.preventDefault()
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const payload = buildReschedulePayload()
    if (!payload) return
    onRescheduleContent?.(payload)
    setDraggedContent(null)
    setHoveredDate(null)
  }

  // Absolute so grid auto-flow doesn't treat these as blockers for span
  // placement. Width matches the grid column under it (accounts for the
  // scrollbar-reserve padding). `pointer-events` gated on drag so idle
  // clicks pass through to the bars.
  const colWidth = `calc((100% - ${SCROLLBAR_RESERVE}px) / ${NUMBER_OF_DAYS})`
  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: `calc(${column - 1} * ${colWidth})`,
        width: colWidth,
        pointerEvents: draggedContent ? 'auto' : 'none',
      }}
      onDragOver={onDragOver}
      onDrop={onDrop}
    />
  )
}

/**
 * The lifted view grows the rows by `WEEK_VIEW_SCROLL_OFFSET`. If that growth
 * removed all the overflow, the browser would reset `scrollTop` to 0 and the
 * view would flip between lifted and unlifted on every scroll. So only lift
 * when more overflow than the offset is left, and once lifted, stay lifted
 * until the rows are back at the top.
 */
export const getMomentsAreScrolled = (
  wasScrolled: boolean,
  {
    scrollTop,
    scrollHeight,
    clientHeight,
  }: Pick<HTMLElement, 'scrollTop' | 'scrollHeight' | 'clientHeight'>
) =>
  scrollTop > 0 &&
  (wasScrolled || scrollHeight - clientHeight > WEEK_VIEW_SCROLL_OFFSET)

type CategoryRowsProps = {
  activeFilters: string[]
  selectedFilters?: Record<string, unknown>
  categories: WeekViewCategory[]
  setMomentsAreScrolled: React.Dispatch<React.SetStateAction<boolean>>
  targetDate: Date
}
export const CategoryRows: React.VFC<CategoryRowsProps> = ({
  activeFilters,
  selectedFilters,
  categories = [],
  setMomentsAreScrolled,
  targetDate,
}) => {
  const categoryRowsContainerRef = React.useRef(null)
  const { selectedSort, spanInMarketDays, viewMode } = useWeekViewSettings()
  const isContentMode = viewMode === WeekViewMode.Content
  const isSpanMode = isContentMode && spanInMarketDays
  const { spanItems, getContent, getMomentForContent } = useContentMap()
  const {
    isLimitedUser,
    isLoadingMoments,
    placeholderMoment,
    setPlaceholderMoment,
  } = React.useContext(WeekViewGlobalContext)

  const handleScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    if (e.target === categoryRowsContainerRef.current) {
      const scroller = e.target as HTMLDivElement
      setMomentsAreScrolled((wasScrolled) =>
        getMomentsAreScrolled(wasScrolled, scroller)
      )
    }
  }

  // Span mode skips ContentStack, so the header's "+ Create"
  // placeholder has nowhere to render — we pick it up per-category below.
  const placeholderDate = placeholderMoment?.scheduled_at
    ? parseISO(placeholderMoment.scheduled_at)
    : null
  const placeholderDayIndex = placeholderDate
    ? differenceInCalendarDays(
        startOfDay(placeholderDate),
        startOfDay(targetDate)
      )
    : -1
  const placeholderDayInRange =
    placeholderDayIndex >= 0 && placeholderDayIndex < NUMBER_OF_DAYS

  return (
    <div
      ref={categoryRowsContainerRef}
      onScroll={handleScroll}
      css={[styles.parentContainer, { width: '100%' }]}
    >
      {categories.map((category) => {
        const spansForCategory = isSpanMode
          ? spanItems.filter((item) => item.categoryId === category.id)
          : []

        const showSpanModeCreator =
          isSpanMode &&
          placeholderMoment != null &&
          placeholderMoment.day_segment === category.id &&
          placeholderDayInRange

        return (
          <React.Fragment key={category.id}>
            <CategoryHeader category={category} />
            <EvenColumns
              styleOverrides={{
                // Explicit columns + row-dense auto-flow give free lane
                // packing for the explicitly-placed span bars below.
                ...(isSpanMode
                  ? {
                      gridTemplateColumns: `repeat(${NUMBER_OF_DAYS}, minmax(0, 1fr))`,
                      gridTemplateRows: 'auto',
                      gridAutoRows: 'auto',
                      gridAutoFlow: 'row dense',
                      paddingBlock: 4,
                      paddingInlineEnd: SCROLLBAR_RESERVE,
                      minBlockSize: 56,
                      position: 'relative',
                      // Span bars (`flex: 1` drag shells) keep their natural
                      // height instead of stretching to the tallest cell in
                      // the row — otherwise opening the creator in a row leaves
                      // gaps below the sibling bars.
                      alignItems: 'start',
                    }
                  : {
                      minHeight:
                        categories.length < 4
                          ? `calc(100%/${categories.length})`
                          : 160,
                    }),
              }}
            >
              {/* Span mode renders bars at the row level and skips the
                  per-cell CategoryColumn chrome entirely. */}
              {!isSpanMode &&
                iterateDays(targetDate, NUMBER_OF_DAYS, (date, index) => {
                  const isMiddleColumn =
                    index === Math.floor(NUMBER_OF_DAYS / 2)
                  return (
                    <CategoryColumn
                      category={category}
                      date={date}
                      key={`${formatDateKey(date)}-${formatCategoryKey(category)}`}
                    >
                      {isContentMode ? (
                        <ContentStack
                          category={category}
                          date={date}
                          allowEmptyStatePlaceholder={isMiddleColumn}
                        />
                      ) : (
                        <MomentStack
                          activeFilters={activeFilters}
                          selectedFilters={selectedFilters}
                          allowNoMomentsPlaceholder={isMiddleColumn}
                          category={category}
                          date={date}
                        />
                      )}
                    </CategoryColumn>
                  )
                })}
              {spansForCategory.map((item) => {
                const content = getContent(item.id)
                if (!content) return null
                const moment = getMomentForContent(item.id)
                return (
                  // `startDayIndex` disambiguates the rare case where one post
                  // yields two spans in the same category (e.g. two same-named
                  // labels), which would otherwise collide on `item.id`.
                  <div
                    key={`${item.id}-${item.startDayIndex}`}
                    css={styles.spanWrapper}
                    style={{
                      gridColumn: `${item.startDayIndex + 1} / span ${item.dayCount}`,
                    }}
                  >
                    <ContentCard
                      content={content}
                      moment={moment}
                      isSpanning
                      clampedLeft={item.clampedLeft}
                      clampedRight={item.clampedRight}
                      isDraggable={
                        Boolean(moment) &&
                        !isLimitedUser &&
                        !isLoadingMoments &&
                        !content?.isSaving &&
                        !moment?.isUpdatingContent
                      }
                    />
                  </div>
                )
              })}
              {showSpanModeCreator && placeholderDate && (
                <div
                  css={styles.spanWrapper}
                  style={{ gridColumn: placeholderDayIndex + 1 }}
                >
                  <ContentQuickCreate
                    date={placeholderDate}
                    sortType={selectedSort.type}
                    categoryId={category.id ?? null}
                    categoryTitle={category.title}
                    onExitForm={() => setPlaceholderMoment(null)}
                  />
                </div>
              )}
              {isSpanMode &&
                spansForCategory.length === 0 &&
                !showSpanModeCreator && (
                  <div
                    role="status"
                    aria-live="polite"
                    aria-label={`${category.title} contains no scheduled content`}
                    css={styles.spanEmptyPill}
                    style={{
                      gridColumn: `${Math.ceil(NUMBER_OF_DAYS / 2)} / span 1`,
                    }}
                  >
                    No scheduled content
                  </div>
                )}
              {/* After bars so they win DOM order during an active drag. */}
              {isSpanMode &&
                iterateDays(targetDate, NUMBER_OF_DAYS, (date, index) => (
                  <SpanDropTarget
                    key={`drop-${formatDateKey(date)}`}
                    date={date}
                    column={index + 1}
                  />
                ))}
            </EvenColumns>
          </React.Fragment>
        )
      })}
    </div>
  )
}
